import { Button } from '@/components/ui/button';
import { Lock, CheckCircle, DownloadSimple, WhatsappLogo, Envelope } from '@phosphor-icons/react';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import {
  buildWhatsAppConsultationUrl,
  buildEmailConsultationHref,
} from '@/lib/constants';

const PRO_FEATURES = [
  'Executive Summary & Management Dashboard',
  'Per-Domain Consultant Analysis',
  'Benchmark vs Typical SME, Mature SME, Best Practice',
  'Top 5 Operational Risks',
  'AI Opportunity Matrix with technology recommendations',
  'SMART-Workflow Maturity Model',
  '30-Day Quick Wins (Week 1–4)',
  '90-Day Transformation Roadmap (3 phases)',
  'Consultant Recommendations + Aurea Advisory',
  'Full 30+ page Professional PDF (delivered after consultation)',
];

const downloadProPdf = (assessmentId) => {
  toast.info('Generating your Professional Report…');
  return apiClient
    .get(`/reports/${assessmentId}/pdf`, { responseType: 'blob' })
    .then(r => {
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SMART_Workflow_Pro_Report_${assessmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Professional Report downloaded!');
    })
    .catch(() => toast.error('Failed to download the report. Please try again.'));
};

const FeatureList = () => (
  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
    {PRO_FEATURES.map(f => (
      <li key={f} className="flex items-start gap-2 text-sm text-slate-200">
        <CheckCircle size={18} weight="fill" className="text-teal-300 flex-shrink-0 mt-0.5" />
        <span>{f}</span>
      </li>
    ))}
  </ul>
);

const consultationContextFrom = (assessment, assessmentId) => ({
  organization: assessment?.respondent?.organization_name,
  contactName: assessment?.respondent?.name,
  assessmentRef: assessmentId,
  awsaiScore: assessment?.overall_score,
  requestedService: 'Professional Diagnostic Report',
});

/**
 * Consultation-based unlock card (Stripe payment intentionally disabled until
 * the launch sales process is validated). Customer requests the Professional
 * Report via WhatsApp or email, with the message pre-populated from their
 * assessment context.
 */
export const UnlockProReportCard = ({ assessmentId, assessment }) => {
  const ctx = consultationContextFrom(assessment, assessmentId);
  const whatsappHref = buildWhatsAppConsultationUrl(ctx);
  const emailHref = buildEmailConsultationHref(ctx);
  return (
    <div
      className="bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 text-white rounded-2xl p-8 shadow-xl mb-8"
      data-testid="unlock-pro-report-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-amber-300/20 border border-amber-300/30 flex items-center justify-center">
            <Lock size={24} weight="fill" className="text-amber-300" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-amber-300 font-bold">Available Upon Consultation</div>
            <h3 className="text-2xl font-bold">Professional Diagnostic Report</h3>
          </div>
        </div>
      </div>
      <p className="text-slate-200 mb-6 max-w-2xl">
        The full 30+ page consulting deliverable — every section, every recommendation,
        and the SMART-Workflow transformation roadmap tailored to your assessment. To ensure quality
        and a personalised briefing, the Professional Report is currently released through a
        short consultation with Aurea Digital Solutions.
      </p>
      <FeatureList />
      <div className="flex flex-col sm:flex-row gap-3 border-t border-white/10 pt-5">
        <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white font-semibold flex-1 sm:flex-initial">
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="unlock-pro-whatsapp-button"
          >
            <WhatsappLogo size={20} weight="fill" className="mr-2" />
            Request via WhatsApp
          </a>
        </Button>
        <Button asChild size="lg" className="bg-amber-300 text-slate-900 hover:bg-amber-200 font-semibold flex-1 sm:flex-initial">
          <a
            href={emailHref}
            data-testid="unlock-pro-email-button"
          >
            <Envelope size={20} weight="bold" className="mr-2" />
            Request via Email
          </a>
        </Button>
      </div>
      <div className="mt-4 text-xs text-slate-400 leading-relaxed">
        Your assessment reference and AWSAI score are pre-filled in the request. Aurea Digital Solutions
        will respond promptly to confirm next steps and deliver the report.
      </div>
    </div>
  );
};

export const PaidUnlockedCard = ({ assessmentId, assessment }) => {
  const ctx = consultationContextFrom(assessment, assessmentId);
  const whatsappHref = buildWhatsAppConsultationUrl(ctx);
  const emailHref = buildEmailConsultationHref(ctx);
  return (
    <div
      className="bg-gradient-to-br from-teal-600 to-emerald-600 text-white rounded-2xl p-8 shadow-xl mb-8"
      data-testid="paid-unlocked-card"
    >
      <div className="flex items-start gap-3 mb-3">
        <CheckCircle size={32} weight="fill" className="text-white flex-shrink-0" />
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-100 font-bold">Report Unlocked</div>
          <h3 className="text-2xl font-bold">Your Professional Diagnostic Report is ready.</h3>
          <p className="text-emerald-50 text-sm mt-1">
            Download the full PDF below, or reach Aurea Digital Solutions to schedule a debrief.
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mt-5">
        <Button
          size="lg"
          onClick={() => downloadProPdf(assessmentId)}
          data-testid="download-pro-pdf-button"
          className="bg-white text-slate-900 hover:bg-slate-100 font-semibold"
        >
          <DownloadSimple size={20} weight="bold" className="mr-2" />
          Download Professional Report
        </Button>
        <Button asChild size="lg" className="bg-green-700 hover:bg-green-800 text-white">
          <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="paid-whatsapp-button">
            <WhatsappLogo size={20} weight="fill" className="mr-2" />
            Book Consultation via WhatsApp
          </a>
        </Button>
        <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
          <a href={emailHref} data-testid="paid-email-button">
            <Envelope size={20} weight="bold" className="mr-2" />
            Email Aurea Digital Team
          </a>
        </Button>
      </div>
    </div>
  );
};
